import { Button, Form, type FormInstance, Spin } from 'antd'
import { useUnmount } from 'ahooks'
import { Trans } from '@lingui/react/macro'

import DescriptionForm from '../DescriptionForm'
import useGetFormKeyPrefix from '../_hooks/useGetFormKeyPrefix'

import styles from './index.module.scss'
import TickSvg from './_components/TickSvg'

import type {
  MusicGeneratorFormValuesNew,
  TaskType,
} from '@/pages/tad.ai/ai-music-generator/_types/form'
import type { Recommend } from '@/pages/tad.ai/ai-music-generator/_store/initialState'

import { useAiMusicGeneratorStoreFromZustand } from '@/pages/tad.ai/ai-music-generator/_store/createStore'
import { FluentFlex } from '@/components/FluentFlex'
import { AI_MUSIC_GENERATOR_MAX_LYRICS_LENGTH } from '@/pages/tad.ai/ai-music-generator/const'
import TipsBaseModal from '@/pages/tad.ai/_blocks/Modals/Tips/TipsBaseModal'


import { useRef, useState, useEffect } from 'react';
import { api } from '@/utils/api';
import { cls } from 'tagged-classnames-free';
interface GenerateLabelsParam {
  prompt?: string
  musicType: 'Song' | 'Instrumental'
  lyrics?: string
  imageUrl?: string
}

interface RecommendFormProps {
  form: FormInstance<MusicGeneratorFormValuesNew>
}
function RecommendForm(props: RecommendFormProps) {
  const { form } = props
  const {
    recommends: smartRecommends,
    customRecommends,
    updateState,
    updateStates,
    lyricsTextareaContent,
    lyricsCustomTextareaContent,
    reloadRecommend,
    finalTaskType,
  } = useAiMusicGeneratorStoreFromZustand()
  const generateRef = useRef<HTMLDivElement>(null)
  const circleRef = useRef<HTMLSpanElement>(null)
  const prompt = Form.useWatch('prompt', form)
  const promptTabsMode = Form.useWatch('promptTabsMode', form) || 'smart'
  const customTaskType = Form.useWatch('customTaskType', form)
  const formKeyPrefix = useGetFormKeyPrefix({ form })
  const descriptionFormKey = `${formKeyPrefix}recommendDescription`
  const labelsSummaryFormKey = `${formKeyPrefix}labelsSummary`
  //跟随smart/custom模式变化的参数
  const {
    taskType = 'Song',
    lyrics,
    recommends,
    showRegenerate,
  } = {
    smart: {
      taskType: finalTaskType,
      lyrics: lyricsTextareaContent,
      recommends: smartRecommends,
      showRegenerate: true,
    },
    custom: {
      taskType: customTaskType,
      lyrics: lyricsCustomTextareaContent,
      recommends: customRecommends,
      showRegenerate: false,
    },
  }[promptTabsMode]

  const imageUrl = Form.useWatch('imageUrl', form)
  const [modelForm] = Form.useForm<MusicGeneratorFormValuesNew>()
  const [selectedRecommendIndex, setSelectedRecommendIndex] = useState(-1)
  const [selectedEditIndex, setSelectedEditIndex] = useState(-1)
  const generateLabelsValidRef = useRef(false)
  const { mutate: generateLabelsMutate, isLoading } =
    api.generation.generateLabels.useMutation<GenerateLabelsParam>({
      onSuccess: (
        data: { title: string; prompt: string; rating: number }[],
      ) => {
        if (generateLabelsValidRef.current) {
          if (promptTabsMode === 'smart') {
            updateStates({
              aiCreateBtnShow: true,
              recommends: data,
            })
          } else {
            updateState('customRecommends', data)
          }
          onSelectRecommend(0, data)
          updateState('reloadRecommend', false)
          generateLabelsValidRef.current = false
        }
      },
    })

  useEffect(() => {
    //generate动画设置
    if (generateRef.current) {
      generateRef.current.addEventListener('click', () => {
        circleRef.current?.classList.add(styles.circle)
        // 动画结束后移除类名，以便下次点击
        circleRef.current?.addEventListener(
          'animationend',
          () => {
            circleRef.current?.classList.remove(styles.circle)
          },
          { once: true },
        )
      })
    }
  }, [])

  function onGenerate(
    cbPrompt?: string,
    cbImageUrl?: string,
    cbLyrics?: string,
  ) {
    generateLabelsValidRef.current = true
    generateLabelsMutate({
      prompt: cbPrompt || prompt || undefined,
      imageUrl: cbImageUrl || imageUrl || undefined,
      musicType: taskType as TaskType,
      lyrics:
        cbLyrics ||
        (taskType === 'Song' &&
          lyrics?.slice(0, AI_MUSIC_GENERATOR_MAX_LYRICS_LENGTH)) ||
        undefined,
      history: (recommends.length && recommends) || undefined,
    })
  }

  useEffect(() => {
    //某些情况下需要自动获取
    if (
      (!recommends.length || reloadRecommend) &&
      (prompt || imageUrl || lyrics)
    ) {
      onGenerate(prompt, imageUrl, lyrics)
    }
  }, [prompt, imageUrl, lyrics, reloadRecommend, recommends.length])

  useEffect(() => {
    //初次挂载判断展示create按钮
    if (recommends.length) {
      updateStates({
        aiCreateBtnShow: true,
      })
      //初次挂载自动选中第一个recommend
      if (selectedRecommendIndex === -1) {
        onSelectRecommend(0)
      }
    }
  }, [recommends.length])

  useUnmount(() => {
    generateLabelsValidRef.current = false
    updateState('reloadRecommend', false)
  })

  function onSelectRecommend(index: number, callBackRecommends?: Recommend[]) {
    const currentRecommends = callBackRecommends || recommends
    setSelectedRecommendIndex(index)
    form.setFieldsValue({
      [descriptionFormKey]: currentRecommends[index].prompt,
      [labelsSummaryFormKey]: currentRecommends[index].title,
    })
  }

  return (
    <FluentFlex className='flex-col space-y-2'>
      <div className='text-f-text-quaternary text-sm leading-5'>
        <Trans>Choose a style</Trans>
      </div>
      <Form.Item name={descriptionFormKey} noStyle></Form.Item>
      <Form.Item name={labelsSummaryFormKey} noStyle></Form.Item>
      <Spin spinning={isLoading}>
        {recommends.map((recommend, index) => (
          <FluentFlex
            key={index}
            onClick={() => onSelectRecommend(index)}
            className={cls`
              group/recommend ${index === selectedRecommendIndex ? 'border-f-border-hover bg-f-bg-hover' : 'border-f-border-secondary'}
                      hover:border-f-border-hover relative my-2 w-full cursor-pointer flex-col space-y-2 rounded-[14px] border p-5
                      text-sm
            `}
          >
            <div className='text-f-text font-semibold leading-5'>
              {recommend.title}
            </div>
            <div className='text-f-text-tertiary leading-5'>
              {recommend.prompt}
            </div>
            <div className='flex items-center gap-[2px] py-1'>
              {Array.from({ length: recommend.rating }, (_, index) => (
                <span
                  key={index}
                  className='i-cus--tad-star-bold text-f-yellow-5 size-4'
                ></span>
              ))}
            </div>
            {index === selectedRecommendIndex && (
              <div
                onClick={(e) => {
                  e.stopPropagation()
                  modelForm.setFieldsValue({
                    description: recommend.prompt,
                  })
                  setSelectedEditIndex(index)
                }}
                className={cls`
                  border-f-border-secondary hover:border-f-border-hover hover:text-f-text text-f-text-quaternary
                        absolute   bottom-5 right-5 rounded-[10px] border px-2 hover:border
                `}
              >
                <span className={cls`i-com--edit-alt`} /> <Trans>Edit</Trans>{' '}
              </div>
            )}
            <TickSvg
              className={cls`${index === selectedRecommendIndex ? 'visible' : 'invisible'} absolute right-0 top-0 !mt-0`}
            />
          </FluentFlex>
        ))}
      </Spin>
      {showRegenerate && (
        <div
          className={cls`mr-auto flex cursor-pointer items-center gap-1 px-2 pb-6 pt-1`}
          ref={generateRef}
          onClick={() => onGenerate()}
        >
          <span className='i-com--revision size-4' ref={circleRef} />
          <span className='text-f-text-tertiary text-sm leading-5'>
            {' '}
            <Trans>Regenerate</Trans>{' '}
          </span>
        </div>
      )}
      <TipsBaseModal
        open={selectedEditIndex !== -1}
        onCancel={() => setSelectedEditIndex(-1)}
        destroyOnHidden
      >
        <div className={cls``}>
          <div className={cls`text-f-text text-lg font-semibold`}>
            {recommends[selectedEditIndex]?.title}
          </div>
          <Form<MusicGeneratorFormValuesNew> form={modelForm}>
            <DescriptionForm
              form={modelForm}
              taskType={taskType}
              hideViewMore
            />
          </Form>
          <div className={cls`flex justify-end gap-3 pt-4`}>
            <Button onClick={() => setSelectedEditIndex(-1)}>
              <Trans>Cancel</Trans>
            </Button>
            <Button
              type='primary'
              onClick={async () => {
                modelForm.validateFields().then((values) => {
                  updateState(
                    'recommends',
                    recommends.map((item, index) =>
                      index === selectedEditIndex
                        ? { ...item, prompt: values.description || '' }
                        : item,
                    ),
                  )
                  if (selectedEditIndex === selectedRecommendIndex) {
                    form.setFieldValue(
                      descriptionFormKey as keyof MusicGeneratorFormValuesNew,
                      values.description,
                    )
                  }
                  setSelectedEditIndex(-1)
                })
              }}
            >
              <Trans>Confirm</Trans>
            </Button>
          </div>
        </div>
      </TipsBaseModal>
    </FluentFlex>
  )
}

export default RecommendForm
